import { Button, Grid, IconButton, InputAdornment, makeStyles, MenuItem, TextField, Typography } from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ClearIcon from '@material-ui/icons/Clear';
import PropTypes from 'prop-types';
import { Search } from '@material-ui/icons';
import { palette } from '../../settings/themes/colors';
import { DEBOUNCE_TIME, INSPECTION_TYPE_OPTIONS } from '../../components/organims/InspectionsFilters';
import { debounce } from 'lodash';
import { SearchInput } from '../../components/molecules/SearchInput';
import { useGetProjects } from '../../queries/queryProjects';
import { getCurrentUser } from '../../utils/auth';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { isFilterActive as hasFilterValue, useActiveFilterClasses } from '../../components/molecules/activeFilter.styles';

const useStyles = makeStyles(Theme => ({
  containerFilter: {
    gap: '10px',
    display: 'flex',
    padding: '0px 20px 10px 20px',
    alignItems: 'center'
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%'
  },
  fieldContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: Theme.spacing(2)
  },
  heading: {
    color: palette.captudataBlue
  },
  searchInput: {
    width: Theme.spacing(60)
  },
  filterItem: {
    minWidth: '150px'
  },
  clearButton: {
    marginLeft: '10px',
    textTransform: 'Capitalize'
  }
}));

export const RESET_FILTER_VALUES = {
  projectId: null,
  type: null,
  status: null,
  query: null
};

const WORK_ORDER_STATUSES = [
  { value: 'todo', label: 'workOrders.orderStatuses.pending' },
  { value: 'in_progress', label: 'workOrders.orderStatuses.inProgress' },
  { value: 'done', label: 'workOrders.orderStatuses.done' },
  { value: 'cancelled', label: 'workOrders.orderStatuses.cancelled' }
];

export const WorkOrderFiltersComponent = ({ onFilterChange = () => {}, clientId }) => {
  const { messages: intlMessages } = useIntl();
  const classes = useStyles();
  const { activeFilter } = useActiveFilterClasses();
  const [byProjectQuery, setByProjectQuery] = useState(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);

  const { data: projects = {} } = useGetProjects(
    {
      clientId,
      params: {
        query: byProjectQuery,
        only_main_attributes: true
      }
    },
    {
      enabled: !!byProjectQuery,
      onSuccess: () => setIsLoadingProjects(false),
      onSettled: () => setIsLoadingProjects(false)
    }
  );
  const [filters, setFilters] = useState(RESET_FILTER_VALUES);
  const [searchInput, setSearchInput] = useState('');
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  const handleFilterChange = (name, value) => {
    const newValue = value === '' ? null : value;
    const newFilters = {
      ...filters,
      [name]: newValue
    };

    setFilters(newFilters);
    onFilterChange({
      ...newFilters,
      page: 1
    });
  };

  const handleSelectProject = project => handleFilterChange('projectId', (project || {}).id || filters.projectId);

  const debouncedSearch = useMemo(
    () =>
      debounce(query => {
        const newFilters = {
          ...filters,
          query: query || null,
          page: 1
        };

        setFilters(newFilters);
        onFilterChange(newFilters);
      }, DEBOUNCE_TIME),
    [filters, onFilterChange]
  );

  const handleSearchChange = e => {
    const value = e.target.value;

    setSearchInput(value);
    debouncedSearch(value);
  };

  const clearSearch = () => {
    setSearchInput('');
    debouncedSearch.cancel();
    const newFilters = {
      ...filters,
      query: null,
      page: 1
    };

    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  useEffect(
    () => () => {
      debouncedSearch.cancel();
    },
    [debouncedSearch]
  );

  useEffect(() => {
    setHasActiveFilters(Object.keys(RESET_FILTER_VALUES).some(key => hasFilterValue(filters[key], RESET_FILTER_VALUES[key])));
  }, [filters]);

  const clearAllFilters = () => {
    setSearchInput('');
    setByProjectQuery(null);
    debouncedSearch.cancel();

    const newFilters = {
      ...RESET_FILTER_VALUES,
      page: 1
    };

    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  return (
    <Grid container spacing={2} className={classes.containerFilter}>
      <Grid item className={classes.container}>
        <Typography variant="h6" className={classes.heading}>
          {intlMessages['common.filters']}
        </Typography>
        <Grid item className={classes.fieldContainer}>
          <Grid item>
            <Typography>{intlMessages['inspections.filter.label.search']}</Typography>
          </Grid>
          <Grid item>
            <TextField
              fullWidth
              variant="outlined"
              className={classNames(classes.searchInput, { [activeFilter]: hasFilterValue(searchInput, RESET_FILTER_VALUES.query) })}
              value={searchInput || ''}
              onChange={handleSearchChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {searchInput && (
                      <IconButton onClick={clearSearch} size="small">
                        <ClearIcon />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={4}>
        <SearchInput
          className={hasFilterValue(filters.projectId, RESET_FILTER_VALUES.projectId) ? activeFilter : undefined}
          value={filters.projectId}
          options={projects.projects}
          onSearch={query => {
            if (query.trim() === '') {
              setByProjectQuery(null);
              return;
            }
            setByProjectQuery(query);
          }}
          onSelect={handleSelectProject}
          isLoading={isLoadingProjects}
          label={intlMessages['inspections.filter.byProject']}
        />
      </Grid>
      <Grid item>
        <TextField
          select
          fullWidth
          label={intlMessages['common.type']}
          value={filters.type || ''}
          onChange={e => handleFilterChange('type', e.target.value)}
          variant="outlined"
          size="small"
          className={classNames(classes.filterItem, { [activeFilter]: hasFilterValue(filters.type, RESET_FILTER_VALUES.type) })}
        >
          <MenuItem value="">{intlMessages['common.all']}</MenuItem>
          {INSPECTION_TYPE_OPTIONS.map(option => (
            <MenuItem key={option} value={option}>
              <FormattedMessage id={`project.inspections.type.${option}`} />
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item>
        <TextField
          select
          fullWidth
          label={intlMessages['common.status']}
          value={filters.status || ''}
          onChange={e => handleFilterChange('status', e.target.value)}
          variant="outlined"
          size="small"
          className={classNames(classes.filterItem, { [activeFilter]: hasFilterValue(filters.status, RESET_FILTER_VALUES.status) })}
        >
          <MenuItem value="">{intlMessages['common.all']}</MenuItem>
          {WORK_ORDER_STATUSES.map(status => (
            <MenuItem key={status.value} value={status.value}>
              {intlMessages[status.label]}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item>
        <Button variant="outlined" onClick={clearAllFilters} disabled={!hasActiveFilters} className={classes.clearButton}>
          <FormattedMessage id="common.resetFilters" />
        </Button>
      </Grid>
    </Grid>
  );
};

WorkOrderFiltersComponent.propTypes = {
  clientId: PropTypes.number,
  onFilterChange: PropTypes.func.isRequired
};

const mapToProps = state => ({ clientId: getCurrentUser(state).client_id });

export const WorkOrderFilters = connect(mapToProps)(WorkOrderFiltersComponent);
